import React from 'react';
import styled from 'styled-components';

import { GROUP_TYPE_CATEGORY_OPTIONS, MANAGER } from 'constants/groupTypes';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { GroupType } from 'models/flexible/GroupType';
import { groupTypesQueryKeys, indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminGroupTypeIndexFields } from 'services/api/fields';
import PencilIcon from 'svg/PencilIcon';

const INCLUDES = [] as const;

const config: AdminListPageConfig<GroupType<AdminGroupTypeIndexFields<(typeof INCLUDES)[number]>>> = {
  title: 'Group Types',
  columnStorageKey: 'admin.groupTypes',
  queryKey: groupTypesQueryKeys.index(),
  fetchFn: (params, signal) => indexGroupTypes(params, signal, [...INCLUDES]),
  includes: INCLUDES,
  fetchParams: { defaultSort: 'name asc' },
  searchPlaceholder: 'Search',
  showResultCount: true,

  addButton: {
    label: 'Add Group Type',
    path: '/admin/group-types/new',
    permission: 'adminGroupTypeCreate',
  },

  filters: [
    {
      key: 'type',
      label: 'Category',
      type: 'dropdown',
      staticOptions: GROUP_TYPE_CATEGORY_OPTIONS.slice(1).map((o) => ({ id: o.value, name: o.label })),
      toRequest: (value) => (value?.id ? value.id : undefined),
    },
    {
      key: 'inpatient',
      label: 'Inpatient',
      type: 'checkbox',
      toRequest: (value) => (value ? 'true' : undefined),
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Standard Name',
      render: (row) => <BoldText>{row.name}</BoldText>,
    },
    { id: 'displayName', header: 'Display Name' },
    {
      id: 'type',
      header: 'Category',
      render: (row) => row.type?.displayName ?? '',
    },
    {
      id: 'isInpatient',
      header: 'Inpatient?',
      render: (row) => (row.type?.apiName === MANAGER ? '—' : row.isInpatient ? 'Yes' : 'No'),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminGroupTypeEdit',
      onClick: (row, { navigate }) => navigate(`/admin/group-types/${row.id}/edit`),
    },
  ],
};

export default function GroupTypes() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}

const BoldText = styled.span`
  font-weight: 500;
`;
